import React from 'react'
import _get from 'lodash/get'
import _set from 'lodash/set'
import { List, Map } from 'immutable'
import { H3 } from '../Heading/index'
import Row, { STYLES } from '../Row.v2/index'
import RowV3 from '../Row.v3/index'
import { getSafeNodeId } from '../../services/node/index'
import { get as getConfig } from '../../config/index'
import Link from '../Link/index'
import {
  getPmSectionTabs,
  MAX_ROW_ITEMS,
  pmSectionTabsHaveData,
} from '../../services/pm-section/index'
import {
  parse as parseQuery,
  stringify as stringifyQuery,
} from '../../services/query-string/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../Picture/index'

const config = getConfig()
const optimizedImagesEnabled = !!_get(config, ['features', 'optimizedImages'])
const rowV3Active = !!_get(config, ['features', 'rowV3'])

/**
 * The gaia plus intro tile component
 * @param {Object} props - The props
 * @param {Map} props.data - The tile data
 * @returns {JSX.Element} The intro tile component
 */
function GaiaPlusIntroTile (props) {
  const { data } = props
  const id = data.get('id')
  const noData = !data || !Map.isMap(data) || !data.size || !id || id <= 0

  if (noData) {
    return (
      <div className="gaia-plus-page__intro-tile-placeholder"></div>
    )
  }

  const url = data.get('url', '')
  const title = data.get('title', '')
  const teaser = data.get('teaser', '')
  const image = data.get('verticalImage')
  const imageWidth = '385'
  const imageHeight = '539'
  let imageUrl = ''
  let fallbackImageUrl = ''

  try {
    if (optimizedImagesEnabled) {
      const fallbackImageUrlObject = new URL(image)
      const imageUrlObject = new URL(image)
      const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
      const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
      const fallbackImageOptions = {
        ...parsedFallbackImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'jpg',
        compress: 'auto',
      }
      const imageOptions = {
        ...parsedImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'webp',
        q: '100',
      }

      const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
      const stringifiedImageQuery = stringifyQuery(imageOptions)

      // set the search string back onto the fallback URL
      _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

      // set the search string back onto the image URL
      _set(imageUrlObject, 'search', stringifiedImageQuery)

      fallbackImageUrl = fallbackImageUrlObject.toString()
      imageUrl = imageUrlObject.toString()
    }
  } catch (e) {
    fallbackImageUrl = image
    imageUrl = image
  }

  return (
    <div className="gaia-plus-page__intro-tile">
      <div className="gaia-plus-page__intro-tile-hero">
        <Link to={url} scrollToTop>
          {
            optimizedImagesEnabled
              ? <Picture
                  fallbackSrc={fallbackImageUrl}
                  width={imageWidth}
                  height={imageHeight}
                  alt={title}
                  loading={IMAGE_LOADING_LAZY}
                  sources={[
                    {
                      type: IMAGE_TYPE_WEBP,
                      srcSet: imageUrl,
                    },
                  ]}
                />
              : <img
                  src={image}
                  loading="lazy"
                  alt={title}
                />
          }
        </Link>
      </div>
      <p className="gaia-plus-page__intro-tile-teaser">
        {teaser}
      </p>
    </div>
  )
}

/**
 * The gaia plus intro tiles component
 * @param {Object} props - The props
 * @param {String} props.sectionId - The id of PM section
 * @param {String} props.language - The user's language
 * @param {Map} props.pmSection - The PM sections data
 * @param {Map} props.series - The series items data
 * @returns {JSX.Element} The intro tiles component
 */

function GaiaPlusIntro (props) {
  const {
    series,
    language,
    pmSection,
    sectionId,
  } = props

  if (!sectionId || !pmSection.size || !series.size) {
    return null
  }

  const section = pmSection.get(sectionId)
  const headerLabel = section.getIn(['data', 'data', 'header', 'label'])
  const footerLabel = section.getIn(['data', 'data', 'footer', 'label'])
  const tabs = getPmSectionTabs({ sectionId, pmSection })
  const listItems = tabs.getIn([0, 'tabContent'], List())
  const hasChildData = pmSectionTabsHaveData({
    tabs,
    series,
    language,
    tabContentMaxItems: MAX_ROW_ITEMS,
  })

  const items = hasChildData
    ? listItems.slice(0, MAX_ROW_ITEMS)
    : List(new Array(MAX_ROW_ITEMS).fill(Map()))

  return (
    <div className="gaia-plus-page__intro">
      <div className="gaia-plus-page__intro-container">
        <H3>{headerLabel}</H3>
        <p className="gaia-plus-page__intro-text">
          {footerLabel}
        </p>
        <div className="gaia-plus-page__intro-tiles">
          {rowV3Active
            ? <RowV3 style={STYLES.TILES_GAIA_PLUS}>
              {items.map((item) => {
                const id = Number(item.get('contentId'))
                return <GaiaPlusIntroTile
                  key={id}
                  data={series.getIn([getSafeNodeId(id), language, 'data'], Map()) || Map()}
                />
              })}
            </RowV3>
            : <Row
              items={items}
              createAccessor={() => null}
              style={STYLES.TILES_GAIA_PLUS}
              disableHover
            >
              {(item) => {
                const id = Number(item.get('contentId'))
                return <GaiaPlusIntroTile
                  data={series.getIn([getSafeNodeId(id), language, 'data'], Map()) || Map()}
                />
              }}
            </Row>
          }
        </div>
      </div>
    </div>
  )
}

export default GaiaPlusIntro
